import axiosInstance from "@/lib/axios-instance";
import { normalizeLocale } from "@/lib/utils";

export interface LoginRequest {
  email: string;
  password: string;
  device_token?: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  country_name: string;
  country_image_png: string;
  country_image_svg: string;
  role_id: number; // 2 for client, 3 for trainer
  agree_terms: number; // 1 for true, 0 for false (matching mobile app format)
  early_access?: number; // 1 (matching mobile app format)
  /** Client signup only — full international number (e.g. +201234567890) */
  phone?: string;
  /** Client signup only — dial code e.g. +20 */
  phone_prefix?: string;
  city?: string;
  /** YYYY-MM-DD */
  date_of_birth?: string;
  /** 0 = female, 1 = male */
  gender?: number;
}

export interface UserData {
  id: number;
  name: string;
  email: string;
  role_id: number;
  is_active: boolean;
  is_verified: boolean;
  is_profile_completed?: boolean;
  is_approved?: boolean;
  is_rejected?: boolean;
  profile_status_message?: string;
  profile_image?: string;
}

export interface LoginResponse {
  token: string;
  firebase_token?: string;
  user_data: UserData;
}

export interface RegisterResponse {
  id?: number;
  name?: string;
  email?: string;
  country_name?: string;
  country_image_png?: string;
  country_image_svg?: string;
  role_id?: number;
  updated_at?: string;
  created_at?: string;
  message?: string; // Fallback if backend only returns message
}

/**
 * Login user using email and password
 * Same endpoint as mobile app: POST /api/login
 * 
 * API Response structure:
 * {
 *   "status": "success",
 *   "code": 200,
 *   "message": "Login complete profile",
 *   "data": {
 *     "token": "...",
 *     "firebase_token": "...",
 *     "user_data": {...}
 *   }
 * }
 * 
 * Note: The axios interceptor returns response.data, so we need to access the raw response
 */
export const login = async (request: LoginRequest): Promise<LoginResponse> => {
  try {
    // Create a new axios instance without the response interceptor to get raw response
    const axios = (await import("axios")).default;
    const { API_BASE_URL } = await import("@/service/api-config");
    
    const language = typeof window !== "undefined" 
      ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
      : "en";

    // Make direct axios call to get full response structure
    const response = await axios.post<{
      status: string;
      code: number;
      message: string;
      data: {
        token: string;
        firebase_token?: string;
        user_data: UserData;
      };
    }>(`${API_BASE_URL}/login`, request, {
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
    });

    // Extract data from response structure
    if (!response.data || !response.data.data) {
      console.error("Invalid login response:", response.data);
      throw new Error("Invalid login response structure: missing data field");
    }

    const loginData: LoginResponse = {
      token: response.data.data.token,
      firebase_token: response.data.data.firebase_token,
      user_data: response.data.data.user_data,
    };

    // Store token in localStorage
    if (loginData.token) {
      localStorage.setItem("token", loginData.token);
      if (loginData.firebase_token) {
        localStorage.setItem("firebase_token", loginData.firebase_token);
      }
    }

    return loginData;
  } catch (error: any) {
    console.error("Login error:", error);
    
    // Extract error message from response if available
    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error.message) {
      throw error;
    }
    throw new Error("Login failed. Please try again.");
  }
};

/**
 * Register new user
 * Same endpoint as mobile app: POST /api/register
 */
export const register = async (
  request: RegisterRequest
): Promise<RegisterResponse> => {
  try {
    // Prepare request matching mobile app format exactly
    const registerPayload: Record<string, unknown> = {
      name: request.name,
      email: request.email,
      password: request.password,
      password_confirmation: request.password_confirmation,
      agree_terms: request.agree_terms,
      early_access: request.early_access ?? 1, // Default to 1 like mobile app
      country_name: request.country_name,
      country_image_png: request.country_image_png,
      country_image_svg: request.country_image_svg,
      role_id: request.role_id,
    };

    if (request.role_id === 2) {
      if (request.phone != null && request.phone !== "") {
        registerPayload.phone = request.phone;
      }
      if (request.phone_prefix != null && request.phone_prefix !== "") {
        registerPayload.phone_prefix = request.phone_prefix;
      }
      if (request.city != null && request.city !== "") {
        registerPayload.city = request.city;
      }
      if (request.date_of_birth != null && request.date_of_birth !== "") {
        registerPayload.date_of_birth = request.date_of_birth;
      }
      if (request.gender !== undefined && request.gender !== null) {
        registerPayload.gender = request.gender;
      }
    }

    // Use raw axios to get full response structure (like login)
    const axios = (await import("axios")).default;
    const { API_BASE_URL } = await import("@/service/api-config");
    
    const language = typeof window !== "undefined" 
      ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
      : "en";

    const response = await axios.post<{
      status?: string;
      code?: number;
      message?: string;
      data?: RegisterResponse;
      error?: { message?: string; status_code?: number };
    }>(`${API_BASE_URL}/register`, registerPayload, {
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
      validateStatus: (status) => status >= 200 && status < 300,
    });

    const body = response.data;

    // Never treat error payloads or missing data as success (avoids false OTP redirect)
    if (body?.error?.message) {
      throw new Error(body.error.message);
    }
    if (body?.status === "error") {
      throw new Error(body.message || "Registration failed. Please try again.");
    }

    if (body?.data) {
      return {
        ...body.data,
        message: body.message || "Registration successful",
      };
    }

    throw new Error(body?.message || "Registration failed. Please try again.");
  } catch (error: any) {
    console.error("Register error:", error);

    const data = error.response?.data;

    // Laravel API handler: { error: { message, status_code } }
    if (data?.error?.message) {
      throw new Error(data.error.message);
    }

    // Laravel validation (422): { message, errors: { field: ["..."] } }
    if (data?.errors && typeof data.errors === "object") {
      const first = Object.values(data.errors as Record<string, string[]>)[0];
      const msg = Array.isArray(first) ? first[0] : first;
      if (msg) {
        throw new Error(String(msg));
      }
    }

    if (data?.message) {
      throw new Error(data.message);
    }
    if (error.message) {
      throw error;
    }
    throw new Error("Registration failed. Please try again.");
  }
};

/**
 * Logout user
 */
export const logout = async (): Promise<void> => {
  try {
    await axiosInstance.post("logout");
  } catch (error: any) {
    console.error("Logout error:", error);
  } finally {
    // Clear tokens from localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("firebase_token");
  }
};

/**
 * Get current user profile data
 * Fetches the latest user data from backend to check profile completion status
 * Uses the login endpoint to refresh user data (which includes is_profile_completed)
 */
export const getCurrentUser = async (): Promise<UserData | null> => {
  if (typeof window === "undefined") return null;
  
  const token = getAuthToken();
  if (!token) return null;

  try {
    const axios = (await import("axios")).default;
    const { API_BASE_URL } = await import("@/service/api-config");
    
    const language = typeof window !== "undefined" 
      ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
      : "en";

    // Fetch authenticated trainer profile which includes is_profile_completed
    // This endpoint uses the auth token to identify the trainer (no ID needed)
    const response = await axios.get<{
      status: string;
      message: string;
      data: {
        trainer_id: number;
        trainer_user_id: number;
        trainer_name: string;
        trainer_email: string;
        trainer_phone?: string;
        is_profile_completed: boolean;
        is_approved: number;
        profile_image?: string;
        [key: string]: any;
      };
    }>(`${API_BASE_URL}/trainers/profile`, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Accept": "application/json",
        "Accept-Language": language,
      },
    });

    if (response.data && response.data.data) {
      const profileData = response.data.data;
      // Return UserData format
      return {
        id: profileData.trainer_user_id,
        name: profileData.trainer_name || "",
        email: profileData.trainer_email || "",
        role_id: 3, // Trainer
        is_active: true,
        is_verified: true,
        is_profile_completed: Boolean(profileData.is_profile_completed),
        is_approved: profileData.is_approved !== undefined ? Boolean(profileData.is_approved) : undefined,
        profile_image: profileData.profile_image,
      };
    }

    return null;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error);
    return null;
    
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  if (typeof window === "undefined") return false;
  return !!localStorage.getItem("token");
};

/**
 * Get stored auth token
 */
export const getAuthToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("token");
};

/**
 * Initialize user data from token if available
 * This is called on page load to restore user session
 */
export const initializeUserFromToken = async (): Promise<UserData | null> => {
  if (typeof window === "undefined") return null;
  
  const token = getAuthToken();
  if (!token) return null;

  try {
    // Try to get user profile from backend
    // Note: This assumes there's a user endpoint, otherwise we'll need to store user data in localStorage
    // For now, we'll rely on the persisted store
    return null;
  } catch (error) {
    console.error("Failed to initialize user from token:", error);
    return null;
  }
};

/**
 * Password Reset Interfaces
 */
export interface ResetPasswordRequest {
  email: string;
}

export interface CheckOTPRequest {
  email: string;
  token: string; // OTP code
}

export interface UpdatePasswordRequest {
  email: string;
  password: string;
  password_confirmation: string;
  otp: string;
}

/**
 * Reset password - sends OTP to email
 * Same endpoint as mobile app: POST /api/password/reset
 */
export const resetPassword = async (
  request: ResetPasswordRequest
): Promise<void> => {
  try {
    const axios = (await import("axios")).default;
    const { API_BASE_URL } = await import("@/service/api-config");

    const language = typeof window !== "undefined" 
      ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
      : "en";

    const response = await axios.post<{
      status: string;
      code: number;
      message: string;
    }>(`${API_BASE_URL}/password/reset`, request, {
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
    });

    if (!response.data || response.data.code !== 200) {
      throw new Error(response.data?.message || "Failed to send reset code");
    }
  } catch (error: any) {
    console.error("Reset password error:", error);
    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error.message) {
      throw error;
    }
    throw new Error("Failed to send password reset code. Please try again.");
  }
};

/**
 * Check OTP code for password reset
 * Same endpoint as mobile app: POST /api/checkOTP
 */
export const checkOTP = async (request: CheckOTPRequest): Promise<void> => {
  try {
    const axios = (await import("axios")).default;
    const { API_BASE_URL } = await import("@/service/api-config");

    const language = typeof window !== "undefined" 
      ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
      : "en";

    const response = await axios.post<{
      status: string;
      code: number;
      message: string;
    }>(`${API_BASE_URL}/checkOTP`, request, {
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
    });

    if (!response.data || response.data.code !== 200) {
      throw new Error(response.data?.message || "Invalid OTP code");
    }
  } catch (error: any) {
    console.error("Check OTP error:", error);
    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error.message) {
      throw error;
    }
    throw new Error("Invalid OTP code. Please try again.");
  }
};

/**
 * Update password after OTP verification
 * Same endpoint as mobile app: POST /api/updatePassword
 */
export const updatePassword = async (
  request: UpdatePasswordRequest
): Promise<void> => {
  try {
    const axios = (await import("axios")).default;
    const { API_BASE_URL } = await import("@/service/api-config");

    const language = typeof window !== "undefined" 
      ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
      : "en";

    const response = await axios.post<{
      status: string;
      code: number;
      message: string;
    }>(`${API_BASE_URL}/updatePassword`, request, {
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
    });

    if (!response.data || response.data.code !== 200) {
      throw new Error(response.data?.message || "Failed to update password");
    }
  } catch (error: any) {
    console.error("Update password error:", error);
    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error.message) {
      throw error;
    }
    throw new Error("Failed to update password. Please try again.");
  }
};

/**
 * Send OTP verification email
 * Same endpoint as mobile app: POST /api/email/verify-sent-code
 */
export const sendVerificationEmail = async (email: string, context: 'signup' | 'forgot_password' = 'signup'): Promise<void> => {
  try {
    const axios = (await import("axios")).default;
    const { API_BASE_URL } = await import("@/service/api-config");
    
    const language = typeof window !== "undefined" 
      ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
      : "en";

    const response = await axios.post<{
      status: string;
      code: number;
      message: string;
    }>(`${API_BASE_URL}/email/verify-sent-code`, {
      email,
      context,
    }, {
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
    });

    const d = response.data;
    const ok = d?.code === 200 || d?.status === "success";
    if (!ok) {
      throw new Error(d?.message || "Failed to send verification email");
    }
  } catch (error: any) {
    console.error("Send verification email error:", error);
    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error.message) {
      throw error;
    }
    throw new Error("Failed to send verification email. Please try again.");
  }
};

/**
 * Verify OTP code for email verification
 * Same endpoint as mobile app: POST /api/email/verify
 */
export const verifyEmailOTP = async (email: string, verificationToken: string): Promise<void> => {
  try {
    const axios = (await import("axios")).default;
    const { API_BASE_URL } = await import("@/service/api-config");
    
    const language = typeof window !== "undefined" 
      ? normalizeLocale(localStorage.getItem("language") || navigator.language || "en")
      : "en";

    const response = await axios.post<{
      status: string;
      code: number;
      message: string;
    }>(`${API_BASE_URL}/email/verify`, {
      email,
      verification_token: verificationToken,
    }, {
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": language,
      },
    });

    if (response.data?.code !== 200) {
      throw new Error(response.data?.message || "Failed to verify OTP");
    }
  } catch (error: any) {
    console.error("Verify OTP error:", error);
    
    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error.message) {
      throw error;
    }
    throw new Error("Failed to verify OTP. Please try again.");
  }
};